"use client";

/**
 * fund-sector-chart.tsx
 *
 * SVG donut chart showing a mutual fund's sector breakdown. Pure SVG — no
 * Chart.js dependency. Shows the top 5 sectors individually, groups the
 * remainder as "Others" in gray. Center of donut displays total stock count.
 *
 * Used in: src/app/funds/[slug]/page.tsx (Portfolio Holdings section)
 */

export interface FundSectorChartProps {
  sectorAllocation: Array<{ sector: string; percentage: number }> | null;
  totalStocks: number | null;
}

const COLORS = [
  "#3B82F6", // blue
  "#8B5CF6", // purple
  "#10B981", // green
  "#F59E0B", // amber
  "#EF4444", // red
];
const OTHERS_COLOR = "#334155";

const SIZE = 200;
const CENTER = SIZE / 2;
const OUTER_R = 90;
const INNER_R = OUTER_R * 0.65;

interface Segment {
  sector: string;
  percentage: number;
  color: string;
}

/** Build an SVG donut arc path from start to end angle (degrees, 0 = top) */
function arcPath(
  startDeg: number,
  endDeg: number,
  outer: number,
  inner: number
): string {
  const start = ((startDeg - 90) * Math.PI) / 180;
  const end = ((endDeg - 90) * Math.PI) / 180;
  const large = endDeg - startDeg > 180 ? 1 : 0;

  const x1 = CENTER + outer * Math.cos(start);
  const y1 = CENTER + outer * Math.sin(start);
  const x2 = CENTER + outer * Math.cos(end);
  const y2 = CENTER + outer * Math.sin(end);
  const x3 = CENTER + inner * Math.cos(end);
  const y3 = CENTER + inner * Math.sin(end);
  const x4 = CENTER + inner * Math.cos(start);
  const y4 = CENTER + inner * Math.sin(start);

  return [
    `M ${x1} ${y1}`,
    `A ${outer} ${outer} 0 ${large} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${inner} ${inner} 0 ${large} 0 ${x4} ${y4}`,
    "Z",
  ].join(" ");
}

export function FundSectorChart({ sectorAllocation, totalStocks }: FundSectorChartProps) {
  if (!sectorAllocation || sectorAllocation.length === 0) {
    return (
      <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
        <p className="text-muted-foreground text-sm">
          Sector allocation data not available.
        </p>
      </div>
    );
  }

  const sorted = [...sectorAllocation].sort((a, b) => b.percentage - a.percentage);
  const top5 = sorted.slice(0, 5);
  const rest = sorted.slice(5);
  const othersPct = rest.reduce((sum, s) => sum + s.percentage, 0);

  const segments: Segment[] = top5.map((s, i) => ({
    sector: s.sector,
    percentage: s.percentage,
    color: COLORS[i],
  }));
  if (othersPct > 0.01) {
    segments.push({ sector: "Others", percentage: othersPct, color: OTHERS_COLOR });
  }

  const totalPct = segments.reduce((sum, s) => sum + s.percentage, 0);

  // Build arcs
  let cursor = 0;
  const arcs = segments.map((seg) => {
    const startDeg = (cursor / totalPct) * 360;
    cursor += seg.percentage;
    const endDeg = (cursor / totalPct) * 360;
    return {
      ...seg,
      path: arcPath(startDeg, endDeg - 0.5, OUTER_R, INNER_R),
    };
  });

  return (
    <div className="bg-surface border border-surface-light/10 rounded-xl p-5">
      <div className="flex flex-col md:flex-row items-center gap-6">
        {/* Donut */}
        <div className="relative shrink-0">
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            {arcs.map((a) => (
              <path key={a.sector} d={a.path} fill={a.color} />
            ))}
          </svg>
          {/* Center text */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-foreground text-3xl font-bold font-tabular leading-none">
              {totalStocks ?? "-"}
            </span>
            <span className="text-muted-foreground text-[10px] uppercase tracking-wide mt-1">
              stocks
            </span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex-1 w-full">
          {segments.map((seg, i) => (
            <div
              key={seg.sector}
              className={`flex items-center justify-between py-2 ${
                i < segments.length - 1
                  ? "border-b border-surface-light/5"
                  : ""
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span
                  className="w-3 h-3 rounded-sm shrink-0"
                  style={{ backgroundColor: seg.color }}
                />
                <span className="text-foreground text-xs truncate">
                  {seg.sector}
                </span>
              </div>
              <span className="text-muted-foreground text-xs font-semibold font-tabular shrink-0 ml-2">
                {seg.percentage.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
